import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  useColorModeValue,
  IconButton,
  Image,
  Center,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiEdit, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

interface SeasonData {
  id: string;
  title: string;
  seasonNumber: string;
  poster: string;
  episodeCount: number;
  description: string;
}

const SeasonManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seriesId, seasonId } = useParams<{ seriesId: string; seasonId: string }>();
  const toast = useToast();
  
  const [season, setSeason] = useState<SeasonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    seasonNumber: '',
    description: ''
  });
  
  // Poster options
  const [posterOption, setPosterOption] = useState<'reuse' | 'new'>('reuse');
  // const [posterFile, setPosterFile] = useState<File | null>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [seriesPoster, setSeriesPoster] = useState<string>('');
  
  // Delete confirmation
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const loadSeason = useCallback(async () => {
    try {
      setLoading(true);
      if (seriesId && seasonId) {
        // Load season from localStorage
        const seasonsKey = `seasons-${seriesId}`;
        const savedSeasons = localStorage.getItem(seasonsKey);
        if (savedSeasons) {
          const seasonsData = JSON.parse(savedSeasons);
          const foundSeason = seasonsData.find((s: SeasonData) => s.id === seasonId);
          if (foundSeason) {
            setSeason(foundSeason);
            setFormData({
              title: foundSeason.title,
              seasonNumber: foundSeason.seasonNumber,
              description: foundSeason.description || ''
            });
            setPosterPreview(foundSeason.poster);
            
            // Load series poster for reuse option
            const seriesData = localStorage.getItem(`series-${seriesId}`);
            if (seriesData) {
              const series = JSON.parse(seriesData);
              setSeriesPoster(series.poster || '');
            }
          } else {
            toast({
              title: 'Season not found',
              description: 'The requested season could not be found.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            navigate(`/series/${seriesId}/manage`);
          }
        }
      }
    } catch (error) {
      console.error('Error loading season:', error);
      toast({
        title: 'Error',
        description: 'Failed to load season details.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [seriesId, seasonId, navigate, toast]);

  useEffect(() => {
    if (seasonId) {
      loadSeason();
    }
  }, [seasonId, loadSeason]);

  const handlePosterSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // setPosterFile(file);
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!season || !user) return;

    try {
      setSaving(true);
      
      // Update season data in localStorage
      const seasonsKey = `seasons-${seriesId}`;
      const savedSeasons = localStorage.getItem(seasonsKey);
      
      if (savedSeasons) {
        const seasonsData = JSON.parse(savedSeasons);
        const updatedSeasons = seasonsData.map((s: SeasonData) => {
          if (s.id === seasonId) {
            return {
              ...s,
              ...formData,
              poster: posterOption === 'reuse' ? seriesPoster : (posterPreview || s.poster)
            };
          }
          return s;
        });
        
        localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
        
        toast({
          title: 'Season updated',
          description: 'Season details have been successfully updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setIsEditing(false);
        loadSeason(); // Reload to show updated data
      }
    } catch (error) {
      console.error('Error updating season:', error);
      toast({
        title: 'Error',
        description: 'Failed to update season.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!season || !user) return;

    try {
      setIsDeleting(true);
      
      // Delete season from localStorage
      const seasonsKey = `seasons-${seriesId}`;
      const savedSeasons = localStorage.getItem(seasonsKey);
      
      if (savedSeasons) {
        const seasonsData = JSON.parse(savedSeasons);
        const updatedSeasons = seasonsData.filter((s: SeasonData) => s.id !== seasonId);
        localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
        
        // Also delete all episodes for this season
        const episodesKey = `episodes-${seriesId}`;
        const savedEpisodes = localStorage.getItem(episodesKey);
        if (savedEpisodes) {
          const episodesData = JSON.parse(savedEpisodes);
          const updatedEpisodes = episodesData.filter((ep: any) => ep.seasonId !== seasonId);
          localStorage.setItem(episodesKey, JSON.stringify(updatedEpisodes));
        }
        
        toast({
          title: 'Season deleted',
          description: 'Season and all its episodes have been deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Navigate back to seasons management
        navigate(`/series/${seriesId}/manage`);
      }
    } catch (error) {
      console.error('Error deleting season:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete season.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onDeleteClose();
    }
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to manage seasons.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center h="50vh">
        <Text>Loading season...</Text>
      </Center>
    );
  }

  if (!season) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Season not found.
          </Text>
          <Button onClick={() => navigate(-1)} colorScheme="purple">
            Go Back
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            {isEditing ? 'Edit Season' : 'Season Management'}
          </Text>
          {!isEditing && (
            <HStack gap={2} ml="auto">
              <Button
                leftIcon={<IconWrapper icon={FiEdit} size={16} />}
                onClick={() => setIsEditing(true)}
                size="sm"
                colorScheme="blue"
                variant="outline"
              >
                Edit
              </Button>
              <Button
                leftIcon={<IconWrapper icon={FiTrash2} size={16} />}
                onClick={onDeleteOpen}
                size="sm"
                colorScheme="red"
                variant="outline"
              >
                Delete
              </Button>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="4xl" mx="auto">
        <VStack spacing={6} align="stretch">
          {/* Season Info Card */}
          <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor={borderColor}>
            <VStack spacing={6} align="stretch">
              {/* Season Poster */}
              <HStack gap={6} align="start">
                <Box position="relative" flex="0 0 200px">
                  <Image
                    src={season.poster || '/placeholder-poster.jpg'}
                    alt={season.title}
                    w="200px"
                    h="120px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="200px"
                        h="120px"
                        bg="gray.200"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.500">No Poster</Text>
                      </Box>
                    }
                  />
                </Box>

                {/* Season Details */}
                <VStack align="start" flex="1" gap={3}>
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {season.title}
                  </Text>
                  <Text fontSize="lg" color={labelColor}>
                    Season {season.seasonNumber}
                  </Text>
                  <Text fontSize="md" color={labelColor}>
                    {season.episodeCount} episodes
                  </Text>
                  {season.description && (
                    <Text color={textColor}>
                      {season.description}
                    </Text>
                  )}
                </VStack>
              </HStack>

              {/* Edit Form */}
              {isEditing && (
                <VStack spacing={4} align="stretch" pt={4} borderTop="1px solid" borderColor={borderColor}>
                  <HStack gap={4}>
                    <FormControl flex="1">
                      <FormLabel color={labelColor}>Season Title</FormLabel>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Enter season title"
                      />
                    </FormControl>
                    <FormControl flex="1">
                      <FormLabel color={labelColor}>Season Number</FormLabel>
                      <Input
                        value={formData.seasonNumber}
                        onChange={(e) => setFormData({ ...formData, seasonNumber: e.target.value })}
                        placeholder="e.g., 1, 2, 3"
                      />
                    </FormControl>
                  </HStack>
                  
                  <FormControl>
                    <FormLabel color={labelColor}>Description</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter season description"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={labelColor}>Season Poster</FormLabel>
                    <RadioGroup value={posterOption} onChange={(value) => setPosterOption(value as 'reuse' | 'new')}>
                      <Stack direction="column" gap={3}>
                        <Radio value="reuse" colorScheme="purple">
                          <VStack align="start" gap={2}>
                            <Text color={textColor}>Reuse series poster</Text>
                            {seriesPoster && (
                              <Image
                                src={seriesPoster}
                                alt="Series poster"
                                w="100px"
                                h="60px"
                                objectFit="cover"
                                borderRadius="md"
                                border="1px solid"
                                borderColor={borderColor}
                              />
                            )}
                          </VStack>
                        </Radio>
                        
                        <Radio value="new" colorScheme="purple">
                          <VStack align="start" gap={2}>
                            <Text color={textColor}>Upload new poster</Text>
                            <HStack gap={3}>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={handlePosterSelect}
                                display="none"
                                id="poster-upload"
                              />
                              <Button
                                as="label"
                                htmlFor="poster-upload"
                                leftIcon={<IconWrapper icon={FiUpload} size={16} />}
                                variant="outline"
                                cursor="pointer"
                                size="sm"
                              >
                                Select Image
                              </Button>
                              {posterPreview && (
                                <Image
                                  src={posterPreview}
                                  alt="Poster preview"
                                  w="100px"
                                  h="60px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor={borderColor}
                                />
                              )}
                            </HStack>
                          </VStack>
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {/* Action Buttons */}
                  <HStack justify="flex-end" gap={3}>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      leftIcon={<IconWrapper icon={FiX} size={16} />}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="purple"
                      onClick={handleSave}
                      isLoading={saving}
                      loadingText="Saving..."
                      leftIcon={<IconWrapper icon={FiSave} size={16} />}
                    >
                      Save Changes
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Season
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{season?.title}"? This action will permanently delete the season and all its episodes. This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete Season
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SeasonManagement;
